import { forwardRef, useMemo } from 'react';
import classnames from 'classnames';

import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { LayoutContext } from '@hh.ru/magritte-ui-grid/layoutContext';
import { GridLayoutProps } from '@hh.ru/magritte-ui-grid/types';

import styles from './layout.less';

const hasResetWidthOnXL = (
    hasLeftSidebar: boolean,
    hasRightSidebar: boolean,
    hideLeft: boolean,
    hideRight: boolean
) => {
    const onlyLeftSidebarHidden = hasLeftSidebar && hideLeft && !hasRightSidebar;
    const onlyRightSidebarHidden = hasRightSidebar && hideRight && !hasLeftSidebar;
    const allSidebarsHidden = hideRight && hideLeft;

    if (onlyLeftSidebarHidden || onlyRightSidebarHidden || allSidebarsHidden) {
        return true;
    }

    return false;
};

export const GridLayout = forwardRef<HTMLDivElement, GridLayoutProps>(
    (
        {
            children,
            hideLeftOnXL = false,
            hideRightOnXL = false,
            rightSidebar,
            leftSidebar,
            leftSidebarDataQA = 'left-sidebar',
            rightSidebarDataQA = 'right-sidebar',
            contentDataQA = 'main-content',
            ...props
        },
        ref
    ) => {
        const { isXL } = useBreakpoint();
        const leftSidebarEnabled = isXL ? !!leftSidebar && !hideLeftOnXL : !!leftSidebar;
        const rightSidebarEnabled = isXL ? !!rightSidebar && !hideRightOnXL : !!rightSidebar;
        const layoutContextValue = useMemo(
            () => ({ leftSidebarEnabled, rightSidebarEnabled }),
            [leftSidebarEnabled, rightSidebarEnabled]
        );
        return (
            <LayoutContext.Provider value={layoutContextValue}>
                <div
                    {...props}
                    className={classnames(styles['grid-layout'], {
                        [styles['grid-layout_with-left-sidebar']]: leftSidebar,
                        [styles['grid-layout_with-right-sidebar']]: rightSidebar,
                        [styles['grid-layout_hide-left-sidebar']]: hideLeftOnXL,
                        [styles['grid-layout_hide-right-sidebar']]: hideRightOnXL,
                        [styles['grid-layout_reset-width']]: hasResetWidthOnXL(
                            !!leftSidebar,
                            !!rightSidebar,
                            hideLeftOnXL,
                            hideRightOnXL
                        ),
                    })}
                    ref={ref}
                >
                    {leftSidebar && (
                        <div className={`${styles['grid-sidebar']} ${styles['grid-sidebar_left']}`}>
                            <div data-qa={leftSidebarDataQA}>{leftSidebar}</div>
                        </div>
                    )}
                    <div className={styles['grid-layout-content']} data-qa={contentDataQA}>
                        {children}
                    </div>
                    {rightSidebar && (
                        <div className={`${styles['grid-sidebar']} ${styles['grid-sidebar_right']}`}>
                            <div data-qa={rightSidebarDataQA}>{rightSidebar}</div>
                        </div>
                    )}
                </div>
            </LayoutContext.Provider>
        );
    }
);

GridLayout.displayName = 'GridLayout';
